import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * Keep the page scroll lock in sync with the dialogs/details that currently
 * require it. This is intentionally query-based rather than counter-based so
 * section replacement, browser history restoration and direct dialog.close()
 * calls cannot leave a stale lock behind.
 */
export function syncDocumentScrollLock() {
  const hasOpenScrollLock = Boolean(document.querySelector('[scroll-lock][open]'));
  document.documentElement.toggleAttribute('scroll-lock', hasOpenScrollLock);
}

let scrollLockSyncQueued = false;

/** Queue one lock reconciliation for the next animation frame. */
export function scheduleDocumentScrollLockSync() {
  if (scrollLockSyncQueued) return;

  scrollLockSyncQueued = true;
  window.requestAnimationFrame(() => {
    scrollLockSyncQueued = false;
    syncDocumentScrollLock();
  });
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {HTMLElement | null} */
  #previouslyFocusedElement = null;

  /** @type {Promise<void> | null} */
  #closingPromise = null;

  /** @type {'keyboard' | 'pointer'} */
  #inputModality = 'pointer';

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }

    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
    this.refs.dialog.addEventListener('close', this.#handleNativeClose);
  }

  disconnectedCallback() {
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }

    const dialog = this.refs?.dialog;

    if (dialog instanceof HTMLDialogElement) {
      dialog.removeEventListener('cancel', this.#handleCancel);
      dialog.removeEventListener('close', this.#handleNativeClose);

      // Shopify can replace sections while a dialog is open. Close it
      // synchronously before the component leaves the document so the native
      // top layer and the shared scroll lock cannot become stranded.
      if (dialog.open) {
        dialog.classList.remove('dialog-closing');
        dialog.close();
      }
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    dialog?.removeAttribute('data-input-modality');
    this.#previouslyFocusedElement = null;
    this.#closingPromise = null;

    scheduleDocumentScrollLockSync();
    super.disconnectedCallback();
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  #handleNativeClose = () => {
    const { dialog } = this.refs;

    this.#syncOpenerExpanded(false);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    dialog.classList.remove('dialog-closing');
    this.dispatchEvent(new DialogCloseEvent());
    this.#restoreFocusWhenAppropriate(dialog);
    scheduleDocumentScrollLockSync();
  };

  /**
   * Resolve the most useful opener from an event, explicit element or current
   * active element.
   * @param {Event | HTMLElement | null | undefined} opener
   * @returns {HTMLElement | null}
   */
  #resolveOpener(opener) {
    if (opener instanceof HTMLElement) return opener;

    if (opener instanceof Event) {
      if (opener.target instanceof HTMLElement) return opener.target;
      if (opener.currentTarget instanceof HTMLElement) return opener.currentTarget;
    }

    return document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }

  /**
   * Resolve whether the dialog was opened through keyboard or pointer input.
   * Click events with detail 0 are generated by keyboard activation.
   * @param {Event | HTMLElement | null | undefined} opener
   * @returns {'keyboard' | 'pointer'}
   */
  #resolveInputModality(opener) {
    if (opener instanceof KeyboardEvent) return 'keyboard';
    if (opener instanceof MouseEvent && opener.detail === 0) return 'keyboard';
    return 'pointer';
  }

  /**
   * Keep the opener's expanded state aligned with the dialog it controls.
   * Some GVS triggers reference the custom-element wrapper while others
   * reference the native dialog, so both IDs are supported.
   * @param {boolean} expanded
   */
  #syncOpenerExpanded(expanded) {
    const opener = this.#previouslyFocusedElement;
    const { dialog } = this.refs;

    if (!(opener instanceof HTMLElement)) return;

    const controlledIds = (opener.getAttribute('aria-controls') || '')
      .split(/\s+/)
      .filter(Boolean);
    const controlsDialog = Boolean(dialog.id && controlledIds.includes(dialog.id));
    const controlsComponent = Boolean(this.id && controlledIds.includes(this.id));

    if (controlsDialog || controlsComponent) {
      opener.setAttribute('aria-expanded', String(expanded));
    }
  }

  #handlePointerDown = () => {
    this.#inputModality = 'pointer';
    this.refs.dialog.dataset.inputModality = 'pointer';
  };

  /**
   * Shows the dialog.
   * @param {Event | HTMLElement | null} [opener]
   */
  showDialog(opener) {
    const { dialog } = this.refs;

    if (dialog.open) return;

    dialog.classList.remove('dialog-closing');
    this.#closingPromise = null;
    this.#previouslyFocusedElement = this.#resolveOpener(opener);
    this.#inputModality = this.#resolveInputModality(opener);
    dialog.dataset.inputModality = this.#inputModality;

    try {
      dialog.showModal();
      this.#syncOpenerExpanded(true);
    } catch (error) {
      // A disconnected or invalid dialog should fail safely rather than leave
      // a caller thinking the page has been locked by an open overlay.
      this.#syncOpenerExpanded(false);
      this.#previouslyFocusedElement = null;
      scheduleDocumentScrollLockSync();
      throw error;
    }

    this.dispatchEvent(new DialogOpenEvent());
    scheduleDocumentScrollLockSync();

    // Wait until the next tick to add the event listeners to avoid a race
    // condition when showDialog is called within the click that opened it.
    window.setTimeout(() => {
      if (!dialog.open || !this.isConnected) return;
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
      this.addEventListener('pointerdown', this.#handlePointerDown, { passive: true });
    });
  }

  /**
   * Closes the dialog.
   * @returns {Promise<void>}
   */
  closeDialog = () => {
    if (this.#closingPromise) return this.#closingPromise;

    const { dialog } = this.refs;

    if (!dialog.open) {
      scheduleDocumentScrollLockSync();
      return Promise.resolve();
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.removeEventListener('pointerdown', this.#handlePointerDown);

    this.#closingPromise = (async () => {
      dialog.classList.add('dialog-closing');

      try {
        await onAnimationEnd(dialog, undefined, { subtree: false });
      } finally {
        if (dialog.open) dialog.close();
        dialog.classList.remove('dialog-closing');
        this.#closingPromise = null;
        scheduleDocumentScrollLockSync();
      }
    })();

    return this.#closingPromise;
  };

  /** Restore focus only when the browser has not already done so and the
   * opener is still visible. This avoids focusing controls hidden inside a
   * closed drawer.
   * @param {HTMLDialogElement} dialog
   */
  #restoreFocusWhenAppropriate(dialog) {
    const previous = this.#previouslyFocusedElement;
    this.#previouslyFocusedElement = null;

    if (!(previous instanceof HTMLElement) || !previous.isConnected) return;

    window.requestAnimationFrame(() => {
      const active = document.activeElement;
      const browserDidNotRestore =
        !active ||
        active === document.body ||
        active === document.documentElement ||
        dialog.contains(active);

      const isVisible = previous.getClientRects().length > 0 && !previous.closest('[inert]');
      const isDisabled = 'disabled' in previous && Boolean(previous.disabled);

      if (browserDidNotRestore && isVisible && !isDisabled) {
        previous.focus({ preventScroll: true });
      }
    });
  }

  /** Toggles the dialog. */
  toggleDialog = (event) => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog(event);
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   * @param {MouseEvent} event
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses Escape.
   * @param {KeyboardEvent} event
   */
  #handleKeyDown(event) {
    this.#inputModality = 'keyboard';
    this.refs.dialog.dataset.inputModality = 'keyboard';

    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /** @returns {number} */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /** @returns {number} */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}


/** Close transient top-layer UI before a page is stored in the back-forward
 * cache. Restoring an `open` dialog from history can otherwise leave the page
 * looking frozen even though its original opener no longer exists. */
function closeOpenScrollLockComponentsForPageLifecycle() {
  document.querySelectorAll('dialog[open]').forEach((dialog) => {
    if (!(dialog instanceof HTMLDialogElement)) return;

    dialog.classList.remove('dialog-closing', 'dialog--closed');

    try {
      dialog.close();
    } catch (error) {
      // Ignore an element that became disconnected during navigation.
    }
  });

  document.querySelectorAll('details[scroll-lock][open]').forEach((details) => {
    details.removeAttribute('open');
  });

  syncDocumentScrollLock();
}

// Native dialog/details changes, direct close() calls and section replacement
// all flow through explicit toggle/close/cancel/lifecycle events. Keep a narrow
// attribute observer only for code that changes the native `open` attribute
// directly; do not observe every child insertion/removal across the document.
document.addEventListener('toggle', scheduleDocumentScrollLockSync, { capture: true });
document.addEventListener('close', scheduleDocumentScrollLockSync, { capture: true });
document.addEventListener('cancel', scheduleDocumentScrollLockSync, { capture: true });
window.addEventListener('pagehide', closeOpenScrollLockComponentsForPageLifecycle);
window.addEventListener('pageshow', scheduleDocumentScrollLockSync);
document.addEventListener('shopify:section:unload', scheduleDocumentScrollLockSync);

const scrollLockObserver = new MutationObserver(scheduleDocumentScrollLockSync);

scrollLockObserver.observe(document.documentElement, {
  subtree: true,
  attributes: true,
  attributeFilter: ['open'],
});

scheduleDocumentScrollLockSync();
